import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, Search, Users, Loader2, ShieldCheck, ArrowLeft, FileText, BarChart3, Mail } from "lucide-react";
import ContentEditor from "@/pages/ContentEditor";
import EmailSettings from "@/pages/EmailSettings";
import { ctaLabel } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#024AD8] focus:ring-4 focus:ring-[#024AD8]/15";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [ctaStats, setCtaStats] = useState(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("leads");

  const fetchLeads = async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken(null);
      } else {
        toast.error("Could not load leads.");
      }
    }
  };

  const fetchStats = async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/cta-stats`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setCtaStats(data.stats);
    } catch {
      // CTA stats are non-critical
    }
  };

  useEffect(() => {
    if (token) {
      fetchLeads(token);
      fetchStats(token);
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (e2) {
      setError(formatDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setLeads(null);
    setCtaStats(null);
  };

  const filtered = useMemo(() => {
    if (!leads) return [];
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.company, l.phone, l.designation, l.city, l.notes, ctaLabel(l.cta_source)]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [leads, query]);

  const exportCsv = () => {
    const header = ["Name", "Email", "Company", "Phone", "Designation", "City", "Source CTA", "Notes", "Submitted At"];
    const rows = filtered.map((l) =>
      [l.name, l.email, l.company, l.phone || "", l.designation || "", l.city || "", l.cta_source ? ctaLabel(l.cta_source) : "", (l.notes || "").replace(/\n/g, " "), l.created_at]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gen2-migration-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"} to CSV.`);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-3xl bg-white p-9 shadow-[0_48px_96px_-32px_rgba(0,0,0,0.6)]">
          <span className="w-12 h-12 rounded-2xl bg-slate-900 grid place-items-center">
            <ShieldCheck className="w-6 h-6 text-[#6D8FF0]" />
          </span>
          <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">Admin sign in</h1>
          <p className="mt-2 text-sm text-slate-500">Access the Gen 2 migration leads dashboard.</p>
          <form onSubmit={handleLogin} className="mt-7 space-y-5">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@hppoly-gen2.com" className={inputClass} />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputClass} />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">{error}</p>
            )}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#024AD8] hover:bg-[#0238A8] disabled:opacity-60 text-white font-semibold px-7 py-3.5 rounded-xl transition-colors duration-300"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <a href="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to landing page
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-md bg-[#024AD8] grid place-items-center font-display font-extrabold text-white text-sm">hp</span>
            <div>
              <p className="font-display font-bold text-slate-900 tracking-tight leading-tight">Gen 2 Leads Dashboard</p>
              <p className="text-xs text-slate-500">Migration inquiries from the landing page</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors hidden sm:block">View site</a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 border border-slate-300 rounded-full px-4 py-2 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex gap-2 mb-8">
          {[["leads", "Leads", Users], ["content", "Site Content", FileText], ["email", "Email", Mail]].map(([key, label, Icon]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                tab === key ? "bg-[#024AD8] text-white border-[#024AD8]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "content" && <ContentEditor token={token} />}

        {tab === "email" && <EmailSettings token={token} />}

        {tab === "leads" && (<>
        <div data-testid="cta-stats-card" className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-xl bg-[#EAF0FC] grid place-items-center">
              <BarChart3 className="w-5 h-5 text-[#024AD8]" />
            </span>
            <div>
              <p className="font-display text-lg font-bold text-slate-900 leading-tight">CTA performance</p>
              <p className="text-xs text-slate-500">Which &quot;Connect with your HP Poly specialist&quot; buttons drive clicks and leads</p>
            </div>
          </div>
          {ctaStats && ctaStats.length > 0 ? (
            <div className="overflow-x-auto">
              <table data-testid="cta-stats-table" className="w-full text-left min-w-[520px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70">
                    {["CTA location", "Clicks", "Leads", "Conversion"].map((h) => (
                      <th key={h} className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...ctaStats].sort((a, b) => b.leads - a.leads || b.clicks - a.clicks).map((s) => (
                    <tr key={s.location} data-testid={`cta-stats-row-${s.location}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{ctaLabel(s.location)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.clicks}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.leads}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-[#024AD8]">
                        {s.clicks ? `${Math.round((s.leads / s.clicks) * 100)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p data-testid="cta-stats-empty" className="text-sm text-slate-500">
              {ctaStats ? "No CTA clicks tracked yet — data appears here as visitors use the buttons." : "Loading…"}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <span className="w-10 h-10 rounded-xl bg-[#EAF0FC] grid place-items-center">
              <Users className="w-5 h-5 text-[#024AD8]" />
            </span>
            <div>
              <p className="font-display text-2xl font-bold text-slate-900 leading-none">{leads ? leads.length : "…"}</p>
              <p className="text-xs text-slate-500 mt-1">Total leads captured</p>
            </div>
          </div>
          <div className="flex flex-1 sm:max-w-md items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="leads-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, company, source…"
                className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#024AD8] focus:ring-4 focus:ring-[#024AD8]/15"
              />
            </div>
            <button
              data-testid="leads-export-button"
              onClick={exportCsv}
              disabled={!filtered.length}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-[#024AD8] disabled:opacity-50 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors duration-300"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div data-testid="leads-table" className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[960px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70">
                  {["Name", "Email", "Company", "Phone", "Designation", "City", "Source", "Notes", "Submitted"].map((h) => (
                    <th key={h} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-4 text-sm font-semibold text-slate-900 whitespace-nowrap">{l.name}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{l.email}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{l.company}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{l.phone || "—"}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{l.designation || "—"}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{l.city || "—"}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">
                      {l.cta_source ? (
                        <span className="inline-flex items-center rounded-full bg-[#EAF0FC] text-[#024AD8] text-xs font-semibold px-2.5 py-1">
                          {ctaLabel(l.cta_source)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500 max-w-[240px] truncate" title={l.notes || ""}>{l.notes || "—"}</td>
                    <td className="px-5 py-4 text-sm text-slate-500 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                  </tr>
                ))}
                {leads && filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-14 text-center text-sm text-slate-500">
                      {leads.length === 0 ? "No leads yet — they will appear here as the form is submitted." : "No leads match your search."}
                    </td>
                  </tr>
                )}
                {!leads && (
                  <tr>
                    <td colSpan={9} className="px-5 py-14 text-center">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>)}
      </main>
    </div>
  );
}
